// src/utils/distanceCalculator.js
import POSTCODE_DISTANCES from './postcodeDistances.json';

class DistanceCalculator {
  constructor() {
    this.cache = new Map();
  }

  async calculateDistance(fromPostcode, toPostcode) {
    // Clean postcodes
    const cleanFrom = this.cleanPostcode(fromPostcode);
    const cleanTo = this.cleanPostcode(toPostcode);
    
    // Same location
    if (cleanFrom === cleanTo) return 0;
    
    // Check cache first
    const cacheKey = `${cleanFrom}-${cleanTo}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }
    
    // 1. Try local database first
    const localDistance = this.getLocalDistance(cleanFrom, cleanTo);
    if (localDistance !== null) {
      this.cache.set(cacheKey, localDistance);
      return localDistance;
    }
    
    // 2. Try API
    try {
      const apiDistance = await this.getApiDistance(cleanFrom, cleanTo);
      this.cache.set(cacheKey, apiDistance);
      return apiDistance;
    } catch (error) {
      console.warn('API failed, using estimate:', error.message);
      
      // 3. Use estimation
      const estimatedDistance = this.estimateDistance(cleanFrom, cleanTo);
      this.cache.set(cacheKey, estimatedDistance);
      return estimatedDistance;
    }
  }

  cleanPostcode(postcode) {
    if (!postcode) return '';
    return postcode.replace(/\s+/g, '').toUpperCase();
  }

  getLocalDistance(from, to) {
    // Extract postcode area (e.g., "SO14" from "SO143TL")
    const fromArea = from.match(/^[A-Z]{1,2}[0-9][A-Z0-9]?/)?.[0];
    const toArea = to.match(/^[A-Z]{1,2}[0-9][A-Z0-9]?/)?.[0];
    
    if (fromArea && toArea && POSTCODE_DISTANCES[fromArea]?.[toArea] !== undefined) {
      return POSTCODE_DISTANCES[fromArea][toArea];
    }
    return null;
  }

  async getApiDistance(from, to) {
    // Get coordinates first
    const fromCoords = await this.getCoordinates(from);
    const toCoords = await this.getCoordinates(to);
    
    if (!fromCoords || !toCoords) {
      throw new Error('Could not get coordinates');
    }
    
    // Calculate straight-line distance
    const distance = this.calculateHaversine(fromCoords, toCoords);
    
    // Add 25% for road distance vs straight line (more accurate for UK)
    return Math.round(distance * 1.25);
  }

  async getCoordinates(postcode) {
    try {
      const response = await fetch(`https://api.postcodes.io/postcodes/${postcode}`);
      
      if (!response.ok) {
        throw new Error('Postcode not found');
      }
      
      const data = await response.json();
      return {
        latitude: data.result.latitude,
        longitude: data.result.longitude
      };
    } catch (error) {
      console.warn(`Could not get coordinates for ${postcode}:`, error.message);
      return null;
    }
  }

  calculateHaversine(coord1, coord2) {
    const R = 3958.8; // Earth's radius in miles
    
    const lat1 = coord1.latitude * Math.PI / 180;
    const lat2 = coord2.latitude * Math.PI / 180;
    const dLat = (coord2.latitude - coord1.latitude) * Math.PI / 180;
    const dLon = (coord2.longitude - coord1.longitude) * Math.PI / 180;
    
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    
    const distance = R * c;
    return Math.round(distance * 10) / 10;
  }

  estimateDistance(from, to) {
    const fromFirst = from.charAt(0);
    const toFirst = to.charAt(0);
    
    if (fromFirst === toFirst) {
      return Math.floor(Math.random() * 10) + 1;
    } else {
      return Math.floor(Math.random() * 100) + 20;
    }
  }
}

const distanceCalculator = new DistanceCalculator();
export default distanceCalculator;